import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import ModuleFrame, { useModuleTab } from '../../components/ModuleFrame';
import { createEntity, updateEntity, deleteEntity, useEntityList, useEntityDoc, friendlyError } from '../../data/entities';
import { apiPost } from '../../utils/backendApi';
import { useAuth } from '../../AuthContext';
import { Markdown, StatusChip, Storyboard, Transcripts, ShotLists, Spec, Inline, filmView, fmtAt } from '../../components/content/FilmViews';
import './ContentPlanningModule.css';

// Content planning — pre-production for films, shorts, decks, articles and
// campaigns (decision content-planning-module). A plan is ten stages; every
// seat can leave a note on a stage or a beat and ask for a refinement; an
// agent drafts the stage again against the org's brand guide and the plan's
// personas; an admin accepts or rejects. Repo-backed plans (nventr's own
// films) write the accepted stage back to git through the repo agent.
//
// Data: orgs/{org}/contentPlans/{plan} + sections/feedback/proposals
// (entity registry: contentPlan, contentSection, contentFeedback,
// contentProposal), orgs/{org}/brandGuides, orgs/{org}/personas. Writes
// that change plan text go through /content/* (backend/api/contentApi.js).

const KINDS = [['film', 'Film'], ['short', 'Short'], ['deck', 'Deck'], ['article', 'Article'], ['campaign', 'Campaign'], ['other', 'Other']];
const PLAN_STATUS = [['proposed', 'Proposed'], ['approved', 'Approved'], ['in-production', 'In production'], ['shipped', 'Shipped'], ['archived', 'Archived']];
const STATUS_ORDER = ['in-production', 'approved', 'proposed', 'shipped', 'archived'];
const SECTION_STATUS = { draft: 'Draft', review: 'In review', approved: 'Approved' };
const VIEWS = [['stages', 'Stages'], ['storyboard', 'Storyboard'], ['transcripts', 'Transcripts'], ['shots', 'Shots'], ['spec', 'Spec'], ['brand', 'Brand'], ['audience', 'Audience']];
const FILM_VIEWS = ['storyboard', 'transcripts', 'shots', 'spec'];

const ago = (t) => {
  if (!t) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const lines = (v) => (Array.isArray(v) ? v.join('\n') : '');
const splitLines = (s) => String(s || '').split('\n').map((x) => x.trim()).filter(Boolean);

// One busy flag per action key; errors surface inline where they happened.
function useActions() {
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});
  const act = async (key, fn) => {
    setBusy((b) => ({ ...b, [key]: true }));
    setErrors((e) => ({ ...e, [key]: '' }));
    try { return await fn(); } catch (err) { setErrors((e) => ({ ...e, [key]: friendlyError(err) })); return null; } finally { setBusy((b) => ({ ...b, [key]: false })); }
  };
  return { busy, errors, act };
}

export default function ContentPlanningModule() {
  const { org, myRole } = useOutletContext();
  const { planId, view = 'stages' } = useParams();
  const isAdmin = ['admin', 'owner'].includes(myRole);
  const canNote = ['member', 'admin', 'owner'].includes(myRole);
  const base = `/orgs/${org.id}/content-planning`;
  const [tab, setTab] = useModuleTab('plans');
  const { docs: plans } = useEntityList('contentPlan', { org: org.id }, { orderBy: 'updatedAt' });
  const { docs: guides } = useEntityList('brandGuide', { org: org.id });
  const { docs: personas } = useEntityList('persona', { org: org.id }, { orderBy: 'name', dir: 'asc' });
  const actions = useActions();

  if (planId) {
    return (
      <PlanPage org={org} base={base} planId={planId} view={VIEWS.some(([k]) => k === view) ? view : 'stages'} plans={plans || []} guides={guides || []} personas={personas || []} isAdmin={isAdmin} canNote={canNote} actions={actions} />
    );
  }

  const list = plans || [];
  const counts = { plans: list.length, brand: (guides || []).length, audience: (personas || []).length };
  return (
    <ModuleFrame
      title="Content planning"
      subtitle="Films, decks and campaigns — planned, reviewed, on brand"
      accent="#fb923c"
      note={`Content planning, tab ${tab}: ${counts.plans} plan${counts.plans === 1 ? '' : 's'}, ${counts.brand} brand guide${counts.brand === 1 ? '' : 's'}, ${counts.audience} persona${counts.audience === 1 ? '' : 's'}. NoVA: plan_content (start a plan), list_contentPlan, list_persona, list_brandGuide; notes, refinements and status are the person's clicks on a plan.`}
      help="Ten stages per plan. Leave a note on any stage or beat, ask for a refinement, accept the proposal. Every proposal is checked against the brand guide and written for the personas the plan is tagged with."
      tabs={[{ id: 'plans', label: 'Plans', count: counts.plans }, { id: 'brand', label: 'Brand', count: counts.brand }, { id: 'audience', label: 'Audience', count: counts.audience }]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: 'Plan', hint: 'Start a film, deck or campaign plan with ten stages.', onClick: () => setTab('plans'), primary: true },
        { label: 'Review', hint: 'Notes on a stage or a beat; request a refinement; accept it.' },
        { label: 'Keep on brand', hint: 'The guide every proposal is checked against.', onClick: () => setTab('brand') },
      ]}
    >
      {tab === 'plans' && <PlansList org={org} base={base} plans={list} guides={guides || []} personas={personas || []} canNote={canNote} actions={actions} />}
      {tab === 'brand' && <BrandTab org={org} guides={guides || []} plans={list} isAdmin={isAdmin} actions={actions} />}
      {tab === 'audience' && <AudienceTab org={org} personas={personas || []} plans={list} canNote={canNote} isAdmin={isAdmin} actions={actions} />}
    </ModuleFrame>
  );
}

// ── Plans list ────────────────────────────────────────────────────────────────
function PlansList({ org, base, plans, guides, personas, canNote, actions }) {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', kind: 'film', surface: '', length: '', spineLine: '' });
  const groups = STATUS_ORDER.map((k) => [k, plans.filter((p) => p.status === k).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))]).filter(([, r]) => r.length);
  const create = () => actions.act('create', async () => {
    const { plan } = await apiPost(`/content/${org.id}/plans`, form);
    setAdding(false);
    setForm({ title: '', kind: 'film', surface: '', length: '', spineLine: '' });
    navigate(`${base}/${plan.id}`);
  });
  return (
    <div className="cp-list">
      <div className="cp-list-head">
        <p className="cs-help">{plans.length ? `${plans.length} plan${plans.length === 1 ? '' : 's'}` : 'No plans yet.'}{guides.length ? '' : ' Add a brand guide on the Brand tab so proposals have rules to check against.'}</p>
        {canNote && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : '+ New plan'}</button>}
      </div>
      {adding && (
        <section className="cs-card cp-card cp-form">
          <h3>New plan</h3>
          <div className="cp-grid-2">
            <label>Title<input className="cs-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Gate film" /></label>
            <label>Kind<select className="cs-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            <label>Where it plays / is read<input className="cs-input" value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} placeholder="/gate page hero" /></label>
            <label>Length<input className="cs-input" value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} placeholder="75 s · 12 slides · 900 words" /></label>
          </div>
          <label>Spine line <span className="cs-help">the one sentence a viewer repeats</span><input className="cs-input" value={form.spineLine} onChange={(e) => setForm({ ...form, spineLine: e.target.value })} /></label>
          {actions.errors.create && <p className="cp-error">{actions.errors.create}</p>}
          <div className="cp-actions">
            <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!form.title.trim() || actions.busy.create} onClick={create}>{actions.busy.create ? 'Creating…' : 'Create plan'}</button>
            <span className="cs-help">Ten stages with starter text for a {form.kind}; the default brand guide is attached.</span>
          </div>
        </section>
      )}
      {groups.map(([k, items]) => (
        <section key={k} className="cp-group">
          <h3><StatusChip k={k}>{PLAN_STATUS.find(([s]) => s === k)?.[1] || k}</StatusChip><span className="ab-count">{items.length}</span></h3>
          <ul className="cp-cards">
            {items.map((p) => {
              const tagged = (p.personaIds || []).map((id) => personas.find((x) => x.id === id)?.name).filter(Boolean);
              return (
                <li key={p.id} className="cs-card cp-plan-card">
                  <Link to={`${base}/${p.id}`} className="cp-plan-title">{p.title}</Link>
                  <p className="cs-help">{KINDS.find(([x]) => x === p.kind)?.[1] || p.kind}{p.length ? ` · ${p.length}` : ''}{p.surface ? ` · ${p.surface}` : ''}</p>
                  {p.spineLine ? <p className="cp-spine">“{p.spineLine}”</p> : null}
                  <div className="cp-plan-foot">
                    {p.openFeedback ? <span className="cp-pill is-open">{p.openFeedback} open note{p.openFeedback === 1 ? '' : 's'}</span> : null}
                    {p.pendingSync ? <span className="cp-pill is-sync">{p.pendingSync.prUrl ? 'PR open' : 'write-back pending'}</span> : null}
                    {p.source?.repoPath ? <span className="cp-pill">repo</span> : null}
                    {tagged.map((n) => <span key={n} className="cp-pill is-persona">{n}</span>)}
                    {(p.segments || []).map((s) => <span key={s} className="cp-pill is-segment">{s}</span>)}
                    <span className="cs-help cp-foot-right">{ago(p.updatedAt)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ── Plan page ─────────────────────────────────────────────────────────────────
function PlanPage({ org, base, planId, view, plans, guides, personas, isAdmin, canNote, actions }) {
  const { doc: plan } = useEntityDoc('contentPlan', { org: org.id }, planId);
  const scope = useMemo(() => ({ org: org.id, contentPlan: planId }), [org.id, planId]);
  const { docs: sections } = useEntityList('contentSection', scope, { orderBy: 'n', dir: 'asc' });
  const { docs: feedback } = useEntityList('contentFeedback', scope);
  const { docs: proposals } = useEntityList('contentProposal', scope);
  const navigate = useNavigate();
  const [stageId, setStageId] = useState(null);
  const ordered = useMemo(() => (sections || []).slice().sort((a, b) => (a.n ?? 99) - (b.n ?? 99)), [sections]);
  useEffect(() => { if (!stageId && ordered.length) setStageId(ordered[0].id); }, [ordered, stageId]);

  if (plan === undefined) return <ModuleFrame title="Content planning" subtitle="Loading…" accent="#fb923c"><p className="cs-help">Loading plan…</p></ModuleFrame>;
  if (!plan) return <ModuleFrame title="Content planning" subtitle="Not found" accent="#fb923c"><p className="cs-help">No plan with that id. <Link className="ab-link" to={base}>Back to plans.</Link></p></ModuleFrame>;

  const film = filmView(plan);
  const hasFilm = film.transcripts.length || film.shotLists.length || film.storyboards.length || film.spec;
  const guide = guides.find((g) => g.id === plan.brandGuideId) || guides.find((g) => g.isDefault) || guides[0] || null;
  const openNotes = (feedback || []).filter((f) => f.status === 'open');
  const setStatus = (status, force = false) => actions.act('status', async () => {
    try { await apiPost(`/content/${org.id}/plans/${planId}/status`, { status, force }); } catch (err) {
      if (/all stages/i.test(err.message || '') && !force && window.confirm(`${err.message}\n\nApprove anyway?`)) return apiPost(`/content/${org.id}/plans/${planId}/status`, { status, force: true });
      throw err;
    }
    return null;
  });
  const sync = () => actions.act('sync', () => apiPost(`/content/${org.id}/plans/${planId}/sync`, {}));
  const tabs = VIEWS.filter(([k]) => !FILM_VIEWS.includes(k) || hasFilm).map(([id, label]) => ({
    id, label,
    count: id === 'stages' && openNotes.length ? openNotes.length : id === 'transcripts' ? (film.transcripts.length + (film.spec?.scripts?.length || 0)) || undefined : id === 'shots' ? film.shotLists.length || undefined : undefined,
  }));
  return (
    <ModuleFrame
      title={plan.title}
      subtitle={`${KINDS.find(([x]) => x === plan.kind)?.[1] || plan.kind}${plan.length ? ` · ${plan.length}` : ''}${plan.surface ? ` · ${plan.surface}` : ''}`}
      accent="#fb923c"
      note={`Content plan "${plan.title}" (id ${plan.id}, ${plan.kind}, status ${plan.status}), ${openNotes.length} open note${openNotes.length === 1 ? '' : 's'}. Ten stages below; leave a note on a stage or beat, request a refinement, accept the proposal. Plan status change is operator-only.`}
      help={plan.spineLine ? `Spine line: “${plan.spineLine}”` : 'No spine line yet — the one sentence a viewer should repeat.'}
      context={(
        <div className="cp-context">
          <Link className="cs-btn cs-btn-sm cs-btn-outline" to={base}>← Plans</Link>
          {isAdmin ? (
            <select className="cs-input cp-status-select" value={plan.status} onChange={(e) => setStatus(e.target.value)} disabled={actions.busy.status} data-agent-guard="plan status">
              {PLAN_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          ) : <StatusChip k={plan.status}>{PLAN_STATUS.find(([s]) => s === plan.status)?.[1] || plan.status}</StatusChip>}
          {plan.pendingSync ? (
            plan.pendingSync.prUrl
              ? <a className="cp-pill is-sync" href={plan.pendingSync.prUrl} target="_blank" rel="noreferrer">PR open ↗</a>
              : <span className="cp-pill is-sync">write-back pending</span>
          ) : null}
          {isAdmin && plan.source?.repoPath ? <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={sync} disabled={actions.busy.sync} title={`Re-read ${plan.source.repoPath} from the repo`}>{actions.busy.sync ? 'Syncing…' : 'Sync from repo'}</button> : null}
        </div>
      )}
      tabs={tabs}
      tab={view}
      onTab={(id) => navigate(`${base}/${planId}${id === 'stages' ? '' : `/${id}`}`)}
    >
      {actions.errors.status && <p className="cp-error">{actions.errors.status}</p>}
      {actions.errors.sync && <p className="cp-error">{actions.errors.sync}</p>}
      {plan.source?.repoPath ? <p className="cs-help cp-source">Repo-backed: <code>{plan.source.repoPath}</code>{plan.source.gitSha ? <> @ <span className="af-mono">{plan.source.gitSha}</span></> : null}{plan.source.statusLine ? <> · {plan.source.statusLine}</> : null}. Accepted stages open a pull request; the plan reads from git again once it merges.</p> : null}
      {view === 'stages' && (
        <Stages base={base} org={org} planId={planId} plan={plan} sections={ordered} feedback={feedback || []} proposals={proposals || []} guide={guide} personas={personas} stageId={stageId} setStageId={setStageId} isAdmin={isAdmin} canNote={canNote} actions={actions} film={film} />
      )}
      {view === 'storyboard' && <section className="cs-card cp-card"><Storyboard plan={film} /></section>}
      {view === 'transcripts' && <section className="cs-card cp-card"><Transcripts plan={film} /></section>}
      {view === 'shots' && <section className="cs-card cp-card"><ShotLists plan={film} /></section>}
      {view === 'spec' && <section className="cs-card cp-card"><Spec plan={film} plans={Object.fromEntries(plans.map((p) => [p.id, p]))} base={base} /></section>}
      {view === 'brand' && (
        <div className="cp-brand-pick">
          {guides.length > 1 && isAdmin ? (
            <label className="cs-help">Guide for this plan{' '}
              <select className="cs-input" value={plan.brandGuideId || ''} onChange={(e) => updateEntity('contentPlan', { org: org.id }, planId, { brandGuideId: e.target.value || null })}>
                {guides.map((g) => <option key={g.id} value={g.id}>{g.name}{g.isDefault ? ' (default)' : ''}</option>)}
              </select>
            </label>
          ) : null}
          {guide ? <GuideView guide={guide} /> : <p className="cs-help">No brand guide in this org yet. Admins add one on the module's Brand tab.</p>}
        </div>
      )}
      {view === 'audience' && <PlanAudience org={org} planId={planId} plan={plan} personas={personas} canNote={isAdmin} />}
    </ModuleFrame>
  );
}

// ── Stages: rail + section body + notes + proposals ───────────────────────────
function Stages({ base, org, planId, plan, sections, feedback, proposals, guide, personas, stageId, setStageId, isAdmin, canNote, actions, film }) {
  const sec = sections.find((s) => s.id === stageId) || sections[0];
  if (!sec) return <p className="cs-help">No stages yet.</p>;
  const notesFor = (id) => feedback.filter((f) => f.sectionId === id);
  const live = (id) => proposals.find((p) => p.sectionId === id && ['queued', 'running', 'proposed'].includes(p.status));
  return (
    <div className="cp-stages">
      <ol className="cp-rail">
        {sections.map((s) => {
          const open = notesFor(s.id).filter((f) => f.status === 'open').length;
          const p = live(s.id);
          return (
            <li key={s.id} className={`cp-rail-item ${s.id === sec.id ? 'is-active' : ''}`}>
              <button type="button" onClick={() => setStageId(s.id)}>
                <span className={`cp-dot is-${s.status || 'draft'} ${p ? `has-${p.status}` : ''}`} aria-hidden="true" />
                <span className="cp-rail-n">{s.n}</span>
                <span className="cp-rail-title">{s.title}</span>
                {open ? <span className="cp-rail-count" title={`${open} open note${open === 1 ? '' : 's'}`}>{open}</span> : null}
                {p ? <span className={`cp-rail-flag is-${p.status}`}>{p.status === 'proposed' ? 'proposal' : p.status}</span> : null}
                {s.pendingSync ? <span className="cp-rail-flag is-sync">PR</span> : null}
              </button>
            </li>
          );
        })}
      </ol>
      <StagePane key={sec.id} base={base} org={org} planId={planId} plan={plan} section={sec} notes={notesFor(sec.id)} proposals={proposals.filter((p) => p.sectionId === sec.id).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))} guide={guide} personas={personas} isAdmin={isAdmin} canNote={canNote} actions={actions} film={film} />
    </div>
  );
}

function StagePane({ base, org, planId, plan, section, notes, proposals, guide, personas, isAdmin, canNote, actions, film }) {
  const { user } = useAuth();
  const [noteOpen, setNoteOpen] = useState(null); // { anchor } | null
  const [note, setNote] = useState('');
  const [refineOpen, setRefineOpen] = useState(false);
  const [remark, setRemark] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const open = notes.filter((f) => f.status === 'open').sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  const inFlight = proposals.find((p) => ['queued', 'running'].includes(p.status));
  const proposed = proposals.find((p) => p.status === 'proposed');
  const past = proposals.filter((p) => ['accepted', 'rejected', 'error'].includes(p.status)).slice(0, 5);
  const path = `/content/${org.id}/plans/${planId}/sections/${section.id}`;
  const addNote = () => actions.act(`note-${section.id}`, async () => {
    await apiPost(`${path}/feedback`, { text: note.trim(), anchor: noteOpen?.anchor || null, kind: 'note' });
    setNote(''); setNoteOpen(null);
  });
  const refine = () => actions.act(`refine-${section.id}`, async () => {
    await apiPost(`${path}/refine`, { remark: remark.trim() });
    setRemark(''); setRefineOpen(false);
  });
  const setSectionStatus = (status) => actions.act(`sstatus-${section.id}`, () => updateEntity('contentSection', { org: org.id, contentPlan: planId }, section.id, { status }));
  const dismiss = (f) => actions.act(`dismiss-${f.id}`, () => updateEntity('contentFeedback', { org: org.id, contentPlan: planId }, f.id, { status: 'dismissed' }));
  // Beats of the transcript stage get their own note anchors.
  const beats = section.stage === 'transcript' ? (film.transcripts[0]?.beats || []).slice(0, 80) : [];
  const nkey = `note-${section.id}`; const rkey = `refine-${section.id}`;
  return (
    <div className="cp-pane">
      <header className="cp-pane-head">
        <div>
          <h2>{section.n}. {section.title}</h2>
          <p className="cs-help">v{section.version || 1} · {SECTION_STATUS[section.status] || section.status}{section.updatedAt ? ` · ${ago(section.updatedAt)}` : ''}{section.pendingSync ? ' · accepted, waiting for the PR to merge' : ''}</p>
        </div>
        <div className="cp-pane-actions">
          {canNote && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => { setNoteOpen(noteOpen && !noteOpen.anchor ? null : { anchor: null }); }}>Note on this section</button>}
          {canNote && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!!inFlight || !!proposed} onClick={() => setRefineOpen((v) => !v)} title={inFlight ? 'A refinement is already running' : proposed ? 'Decide on the open proposal first' : `Draft this stage again from ${open.length} open note${open.length === 1 ? '' : 's'}`}>Request refinement</button>}
          {isAdmin && (
            <select className="cs-input cp-status-select" value={section.status || 'draft'} onChange={(e) => setSectionStatus(e.target.value)} disabled={actions.busy[`sstatus-${section.id}`]}>
              {Object.entries(SECTION_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
        </div>
      </header>

      {noteOpen && !noteOpen.anchor && (
        <NoteBox value={note} onChange={setNote} onSend={addNote} onClose={() => setNoteOpen(null)} busy={actions.busy[nkey]} error={actions.errors[nkey]} label={`Note on ${section.title}`} />
      )}
      {refineOpen && (
        <section className="cs-card cp-card cp-box">
          <h4>Request refinement <span className="cs-help">· {open.length} open note{open.length === 1 ? '' : 's'} go with it{guide ? ` · checked against “${guide.name}”` : ' · no brand guide'}{plan.personaIds?.length ? ` · written for ${plan.personaIds.map((id) => personas.find((p) => p.id === id)?.name).filter(Boolean).join(', ')}` : ''}</span></h4>
          <textarea className="cs-input" rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Optional: what should change, in a sentence. The open notes are included either way." />
          {actions.errors[rkey] && <p className="cp-error">{actions.errors[rkey]}</p>}
          <div className="cp-actions">
            <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={actions.busy[rkey] || (!open.length && remark.trim().length < 8)} onClick={refine}>{actions.busy[rkey] ? 'Sending…' : 'Send to the agent'}</button>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => setRefineOpen(false)}>Cancel</button>
            {!open.length && remark.trim().length < 8 ? <span className="cs-help">Leave a note or write a remark first.</span> : null}
          </div>
        </section>
      )}

      {inFlight && (
        <section className="cs-card cp-card cp-proposal is-running">
          <h4><span className="cp-spinner" aria-hidden="true" /> {inFlight.status === 'queued' ? 'Waiting for the agent…' : 'Drafting…'} <span className="cs-help">{inFlight.feedbackIds?.length || 0} note{inFlight.feedbackIds?.length === 1 ? '' : 's'} · asked {ago(inFlight.createdAt)}</span></h4>
          {inFlight.brief ? <p className="cp-brief">{inFlight.brief}</p> : null}
        </section>
      )}
      {proposed && <ProposalCard org={org} planId={planId} p={proposed} isAdmin={isAdmin} actions={actions} />}

      <section className="cs-card cp-card cp-body">
        <Markdown text={section.body || '_Empty — nothing written for this stage yet._'} base={base} />
      </section>

      {beats.length ? (
        <section className="cs-card cp-card">
          <h4>Beats <span className="cs-help">· notes can land on a line</span></h4>
          <ol className="cp-beats">
            {beats.map((b, i) => {
              const anchor = `beat:${b.at ?? i}`;
              const here = notes.filter((f) => f.anchor === anchor && f.status === 'open');
              return (
                <li key={i} className={`cp-beat ${here.length ? 'has-notes' : ''}`}>
                  <span className="af-beat-at">{fmtAt(b.at)}</span>
                  <div className="cp-beat-body">
                    <p>{b.text}</p>
                    {here.map((f) => <p key={f.id} className="cp-beat-note">↳ {f.text}</p>)}
                    {noteOpen?.anchor === anchor && <NoteBox value={note} onChange={setNote} onSend={addNote} onClose={() => setNoteOpen(null)} busy={actions.busy[nkey]} error={actions.errors[nkey]} label={`Note on the beat at ${fmtAt(b.at)}`} compact />}
                  </div>
                  {canNote && noteOpen?.anchor !== anchor ? <button type="button" className="cp-beat-btn" onClick={() => { setNoteOpen({ anchor }); setNote(''); }}>Note on this beat</button> : null}
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}

      <section className="cs-card cp-card">
        <h4>Notes <span className="cs-help">· {open.length} open · {notes.length - open.length} resolved</span></h4>
        {open.length ? (
          <ul className="cp-notes">
            {open.map((f) => (
              <li key={f.id} className={`cp-note is-${f.kind || 'note'}`}>
                <div className="cp-note-meta"><span className="cp-pill">{f.kind === 'refinement' ? 'remark' : f.anchor ? f.anchor.replace('beat:', 'beat @') : 'section'}</span><span className="cs-help">{ago(f.createdAt)}</span></div>
                <p>{f.text}</p>
                {(isAdmin || f.createdBy === user?.uid) && <button type="button" className="cp-link" onClick={() => dismiss(f)} disabled={actions.busy[`dismiss-${f.id}`]}>dismiss</button>}
              </li>
            ))}
          </ul>
        ) : <p className="cs-help">No open notes on this stage.</p>}
        {notes.length - open.length > 0 ? (
          <details className="cp-resolved"><summary className="cs-help">{notes.length - open.length} resolved / dismissed</summary>
            <ul className="cp-notes is-muted">{notes.filter((f) => f.status !== 'open').map((f) => <li key={f.id} className="cp-note"><div className="cp-note-meta"><span className="cp-pill">{f.status}</span><span className="cs-help">{ago(f.createdAt)}</span></div><p>{f.text}</p></li>)}</ul>
          </details>
        ) : null}
      </section>

      {(past.length || (section.history || []).length) ? (
        <section className="cs-card cp-card">
          <button type="button" className="cp-link" onClick={() => setShowHistory((v) => !v)}>{showHistory ? 'Hide' : 'Show'} history · {past.length} past proposal{past.length === 1 ? '' : 's'} · {(section.history || []).length} earlier version{(section.history || []).length === 1 ? '' : 's'}</button>
          {showHistory && (
            <>
              {past.map((p) => <ProposalCard key={p.id} org={org} planId={planId} p={p} isAdmin={false} actions={actions} compact />)}
              {(section.history || []).slice().reverse().map((h) => (
                <details key={h.version} className="cp-version"><summary>v{h.version} · {ago(h.at)}{h.rationale ? ` · ${h.rationale.slice(0, 120)}` : ''}</summary><pre className="lab-md">{h.body}</pre></details>
              ))}
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}

function NoteBox({ value, onChange, onSend, onClose, busy, error, label, compact }) {
  return (
    <div className={`cp-box ${compact ? 'is-compact' : 'cs-card cp-card'}`}>
      {!compact && <h4>{label}</h4>}
      <textarea className="cs-input" rows={compact ? 2 : 3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={compact ? label : 'What should change, and why. Short is fine.'} autoFocus />
      {error && <p className="cp-error">{error}</p>}
      <div className="cp-actions">
        <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || value.trim().length < 3} onClick={onSend}>{busy ? 'Adding…' : 'Add note'}</button>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// before → after, rationale, one line per brand rule; Accept/Reject for admins.
function ProposalCard({ org, planId, p, isAdmin, actions, compact }) {
  const [side, setSide] = useState(false);
  const key = `decide-${p.id}`;
  const decide = (verb) => actions.act(key, () => apiPost(`/content/${org.id}/plans/${planId}/proposals/${p.id}/${verb}`, {}));
  const failed = (p.brandChecks || []).filter((c) => c.ok === false).length;
  return (
    <section className={`cs-card cp-card cp-proposal is-${p.status} ${compact ? 'is-compact' : ''}`}>
      <header className="cp-proposal-head">
        <h4>{p.status === 'proposed' ? 'Proposal' : p.status === 'accepted' ? 'Accepted' : p.status === 'rejected' ? 'Rejected' : p.status === 'error' ? 'Failed' : p.status} <span className="cs-help">· {ago(p.createdAt)} · {p.feedbackIds?.length || 0} note{p.feedbackIds?.length === 1 ? '' : 's'}{p.usage?.model ? ` · ${p.usage.model}` : ''}</span></h4>
        {!compact && <label className="cs-help cp-toggle"><input type="checkbox" checked={side} onChange={(e) => setSide(e.target.checked)} /> side by side</label>}
      </header>
      {p.status === 'error' ? <p className="cp-error">{p.error || 'The agent did not return a draft.'}</p> : null}
      {p.unchanged ? <p className="cs-help">The agent proposed no change to the text.</p> : null}
      {p.after || p.before ? (
        <div className={`lab-diff cp-diff ${side ? 'is-side' : ''}`}>
          <div><span className="lab-diff-k">Before</span><div className="lab-diff-before">{p.before || <em>empty</em>}</div></div>
          <div><span className="lab-diff-k">After</span><div className="lab-diff-after">{p.after || <em>no text</em>}</div></div>
        </div>
      ) : null}
      {p.rationale ? <p className="cp-rationale"><strong>Why:</strong> {p.rationale}</p> : null}
      {(p.brandChecks || []).length ? (
        <ul className="cp-checks">
          {p.brandChecks.map((c, i) => <li key={i} className={c.ok ? 'is-ok' : 'is-fail'}><span aria-hidden="true">{c.ok ? '✓' : '✗'}</span> <span className="cp-check-rule">{c.rule}</span>{c.note ? <span className="cs-help"> — {c.note}</span> : null}</li>)}
        </ul>
      ) : null}
      {actions.errors[key] && <p className="cp-error">{actions.errors[key]}</p>}
      {p.status === 'proposed' && isAdmin ? (
        <div className="cp-actions">
          <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={actions.busy[key]} onClick={() => decide('accept')} data-agent-guard="accept proposal">{actions.busy[key] ? 'Working…' : `Accept${failed ? ` (${failed} brand check${failed === 1 ? '' : 's'} failed)` : ''}`}</button>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={actions.busy[key]} onClick={() => decide('reject')}>Reject</button>
          <span className="cs-help">Accept makes this the stage's next version; Reject reopens the notes.</span>
        </div>
      ) : p.status === 'proposed' ? <p className="cs-help">Waiting for an admin to accept or reject.</p> : null}
      {p.decidedAt ? <p className="cs-help">{p.status} {ago(p.decidedAt)}{p.reason ? ` — ${p.reason}` : ''}</p> : null}
    </section>
  );
}

// ── Brand ─────────────────────────────────────────────────────────────────────
const GUIDE_BLANK = { name: '', isDefault: false, positioning: [], honesty: [], voice: { register: '', pronunciations: [], mustSay: [], neverSay: [], rules: [] }, visual: { palette: [], type: '', lookPrefix: '', negative: '', camera: '' }, narrator: { name: '', instructions: '' } };

function BrandTab({ org, guides, plans, isAdmin, actions }) {
  const [editing, setEditing] = useState(null); // guide id | 'new'
  const sorted = guides.slice().sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0) || String(a.name).localeCompare(String(b.name)));
  const usedBy = (g) => plans.filter((p) => p.brandGuideId === g.id).length;
  if (editing) {
    const guide = editing === 'new' ? null : guides.find((g) => g.id === editing);
    return <GuideEditor org={org} guide={guide} onClose={() => setEditing(null)} actions={actions} firstGuide={!guides.length} />;
  }
  return (
    <div className="cp-list">
      <div className="cp-list-head">
        <p className="cs-help">The guide every proposal is checked against: positioning, honesty rules, voice, visual look, narrator. Admins edit; everyone reads.</p>
        {isAdmin && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => setEditing('new')}>+ Brand guide</button>}
      </div>
      {!sorted.length ? <p className="cs-help">No brand guide yet.{isAdmin ? ' Add one — proposals without a guide are drafted on notes alone.' : ''}</p> : null}
      {sorted.map((g) => (
        <section key={g.id} className="cs-card cp-card">
          <header className="cp-pane-head">
            <div><h3>{g.name}{g.isDefault ? <span className="cp-pill is-default">default</span> : null}</h3><p className="cs-help">used by {usedBy(g)} plan{usedBy(g) === 1 ? '' : 's'} · {ago(g.updatedAt || g.createdAt)}</p></div>
            {isAdmin && <div className="cp-pane-actions">
              {!g.isDefault && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => actions.act(`default-${g.id}`, async () => { for (const o of guides) if (o.isDefault) await updateEntity('brandGuide', { org: org.id }, o.id, { isDefault: false }); await updateEntity('brandGuide', { org: org.id }, g.id, { isDefault: true }); })}>Make default</button>}
              <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => setEditing(g.id)}>Edit</button>
            </div>}
          </header>
          <GuideView guide={g} />
        </section>
      ))}
    </div>
  );
}

function GuideView({ guide: g }) {
  const List = ({ items }) => (items?.length ? <ul className="cp-rules">{items.map((r, i) => <li key={i}><Inline text={r} /></li>)}</ul> : <p className="cs-help">—</p>);
  return (
    <div className="cp-guide">
      <div className="cp-guide-col"><h5>Positioning</h5><List items={g.positioning} /></div>
      <div className="cp-guide-col"><h5>Honesty</h5><List items={g.honesty} /></div>
      <div className="cp-guide-col">
        <h5>Voice</h5>
        {g.voice?.register ? <p>{g.voice.register}</p> : null}
        {g.voice?.pronunciations?.length ? <p className="cs-help">Say: {g.voice.pronunciations.map((p) => `${p.word} → “${p.say}”`).join(' · ')}</p> : null}
        {g.voice?.mustSay?.length ? <p><strong>Say:</strong> {g.voice.mustSay.join(' · ')}</p> : null}
        {g.voice?.neverSay?.length ? <p><strong>Never:</strong> {g.voice.neverSay.join(' · ')}</p> : null}
        <List items={g.voice?.rules} />
      </div>
      <div className="cp-guide-col">
        <h5>Visual</h5>
        {g.visual?.palette?.length ? <p className="cs-help">{g.visual.palette.join(' · ')}</p> : null}
        {g.visual?.type ? <p>{g.visual.type}</p> : null}
        {g.visual?.camera ? <p>{g.visual.camera}</p> : null}
        {g.visual?.lookPrefix ? <details><summary className="cs-help">lookPrefix</summary><pre className="lab-md">{g.visual.lookPrefix}</pre></details> : null}
        {g.visual?.negative ? <details><summary className="cs-help">negative</summary><pre className="lab-md">{g.visual.negative}</pre></details> : null}
      </div>
      <div className="cp-guide-col">
        <h5>Narrator</h5>
        {g.narrator?.name ? <p>{g.narrator.name}</p> : null}
        {g.narrator?.instructions ? <details><summary className="cs-help">instructions</summary><pre className="lab-md">{g.narrator.instructions}</pre></details> : null}
        {g.narrator?.executiveVariant ? <p className="cs-help">Executive variant: {g.narrator.executiveVariant}</p> : null}
      </div>
    </div>
  );
}

function GuideEditor({ org, guide, onClose, actions, firstGuide }) {
  const { user } = useAuth();
  const [g, setG] = useState(() => ({ ...GUIDE_BLANK, ...(guide || {}), isDefault: guide ? !!guide.isDefault : firstGuide, voice: { ...GUIDE_BLANK.voice, ...(guide?.voice || {}) }, visual: { ...GUIDE_BLANK.visual, ...(guide?.visual || {}) }, narrator: { ...GUIDE_BLANK.narrator, ...(guide?.narrator || {}) } }));
  const [pron, setPron] = useState((guide?.voice?.pronunciations || []).map((p) => `${p.word} = ${p.say}`).join('\n'));
  const key = 'guide-save';
  const set = (k, v) => setG((x) => ({ ...x, [k]: v }));
  const setIn = (k, kk, v) => setG((x) => ({ ...x, [k]: { ...x[k], [kk]: v } }));
  const save = () => actions.act(key, async () => {
    const payload = {
      name: g.name.trim(), isDefault: !!g.isDefault, positioning: g.positioning, honesty: g.honesty,
      voice: { ...g.voice, pronunciations: splitLines(pron).map((l) => { const [word, say] = l.split('=').map((s) => s.trim()); return word && say ? { word, say } : null; }).filter(Boolean) },
      visual: g.visual, narrator: g.narrator,
    };
    if (guide) await updateEntity('brandGuide', { org: org.id }, guide.id, payload);
    else await createEntity('brandGuide', { org: org.id }, { ...payload, createdBy: user.uid });
    onClose();
  });
  const Lines = ({ label, value, onChange, rows = 4, hint }) => (
    <label>{label}{hint ? <span className="cs-help"> · {hint}</span> : null}<textarea className="cs-input" rows={rows} value={lines(value)} onChange={(e) => onChange(splitLines(e.target.value))} /></label>
  );
  return (
    <section className="cs-card cp-card cp-form">
      <header className="cp-pane-head"><h3>{guide ? `Edit “${guide.name}”` : 'New brand guide'}</h3><button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={onClose}>Cancel</button></header>
      <div className="cp-grid-2">
        <label>Name<input className="cs-input" value={g.name} onChange={(e) => set('name', e.target.value)} /></label>
        <label className="cp-check"><input type="checkbox" checked={!!g.isDefault} onChange={(e) => set('isDefault', e.target.checked)} /> Default guide for new plans</label>
      </div>
      <Lines label="Positioning" hint="one rule per line — what we are, what we are not" value={g.positioning} onChange={(v) => set('positioning', v)} />
      <Lines label="Honesty" hint="one rule per line — what may appear and on what evidence" value={g.honesty} onChange={(v) => set('honesty', v)} />
      <h4>Voice</h4>
      <label>Register<textarea className="cs-input" rows={2} value={g.voice.register} onChange={(e) => setIn('voice', 'register', e.target.value)} /></label>
      <div className="cp-grid-2">
        <label>Pronunciations <span className="cs-help">· word = how to say it, one per line</span><textarea className="cs-input" rows={3} value={pron} onChange={(e) => setPron(e.target.value)} /></label>
        <Lines label="Voice rules" value={g.voice.rules} onChange={(v) => setIn('voice', 'rules', v)} rows={3} />
        <Lines label="Must say" value={g.voice.mustSay} onChange={(v) => setIn('voice', 'mustSay', v)} rows={3} />
        <Lines label="Never say" value={g.voice.neverSay} onChange={(v) => setIn('voice', 'neverSay', v)} rows={3} />
      </div>
      <h4>Visual</h4>
      <div className="cp-grid-2">
        <Lines label="Palette / materials" value={g.visual.palette} onChange={(v) => setIn('visual', 'palette', v)} rows={3} />
        <label>Type / UI rule<textarea className="cs-input" rows={3} value={g.visual.type} onChange={(e) => setIn('visual', 'type', e.target.value)} /></label>
        <label>Camera<textarea className="cs-input" rows={3} value={g.visual.camera} onChange={(e) => setIn('visual', 'camera', e.target.value)} /></label>
        <label>Look prefix <span className="cs-help">· pasted into image / video prompts</span><textarea className="cs-input" rows={3} value={g.visual.lookPrefix} onChange={(e) => setIn('visual', 'lookPrefix', e.target.value)} /></label>
        <label>Negative<textarea className="cs-input" rows={3} value={g.visual.negative} onChange={(e) => setIn('visual', 'negative', e.target.value)} /></label>
      </div>
      <h4>Narrator</h4>
      <div className="cp-grid-2">
        <label>Name / voice<input className="cs-input" value={g.narrator.name} onChange={(e) => setIn('narrator', 'name', e.target.value)} /></label>
        <label>Instructions<textarea className="cs-input" rows={4} value={g.narrator.instructions} onChange={(e) => setIn('narrator', 'instructions', e.target.value)} /></label>
      </div>
      {actions.errors[key] && <p className="cp-error">{actions.errors[key]}</p>}
      <div className="cp-actions">
        <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!g.name.trim() || actions.busy[key]} onClick={save}>{actions.busy[key] ? 'Saving…' : 'Save guide'}</button>
        <span className="cs-help">Proposals requested after this save are checked against the new rules.</span>
      </div>
    </section>
  );
}

// ── Audience ──────────────────────────────────────────────────────────────────
const PERSONA_BLANK = { name: '', role: '', cares: [], objections: [], proofWanted: [], channels: [], spineLine: '' };

function AudienceTab({ org, personas, plans, canNote, isAdmin, actions }) {
  const [editing, setEditing] = useState(null);
  const usedBy = (p) => plans.filter((x) => (x.personaIds || []).includes(p.id)).length;
  if (editing) return <PersonaEditor org={org} persona={editing === 'new' ? null : personas.find((p) => p.id === editing)} onClose={() => setEditing(null)} actions={actions} isAdmin={isAdmin} />;
  return (
    <div className="cp-list">
      <div className="cp-list-head">
        <p className="cs-help">Who the plans are for. A persona carries what they care about, what they object to, the proof they want and the one line they should repeat; the agent writes every proposal for the personas the plan is tagged with.</p>
        {canNote && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => setEditing('new')}>+ Persona</button>}
      </div>
      {!personas.length ? <p className="cs-help">No personas yet.</p> : null}
      <ul className="cp-cards cp-personas">
        {personas.map((p) => (
          <li key={p.id} className="cs-card cp-card cp-persona">
            <header className="cp-pane-head">
              <div><h3>{p.name}</h3><p className="cs-help">{p.role}</p></div>
              {canNote && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => setEditing(p.id)}>Edit</button>}
            </header>
            {p.spineLine ? <p className="cp-spine">“{p.spineLine}”</p> : null}
            <PersonaView p={p} />
            <p className="cs-help">tagged on {usedBy(p)} plan{usedBy(p) === 1 ? '' : 's'}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PersonaView({ p }) {
  const Row = ({ label, items }) => (items?.length ? <p><strong>{label}:</strong> {items.join(' · ')}</p> : null);
  return (
    <div className="cp-persona-body">
      <Row label="Cares about" items={p.cares} />
      <Row label="Objects to" items={p.objections} />
      <Row label="Wants proof" items={p.proofWanted} />
      <Row label="Reached via" items={p.channels} />
    </div>
  );
}

function PersonaEditor({ org, persona, onClose, actions, isAdmin }) {
  const { user } = useAuth();
  const [p, setP] = useState(() => ({ ...PERSONA_BLANK, ...(persona || {}) }));
  const key = 'persona-save';
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const save = () => actions.act(key, async () => {
    const payload = { name: p.name.trim(), role: p.role, cares: p.cares, objections: p.objections, proofWanted: p.proofWanted, channels: p.channels, spineLine: p.spineLine };
    if (persona) await updateEntity('persona', { org: org.id }, persona.id, payload);
    else await createEntity('persona', { org: org.id }, { ...payload, createdBy: user.uid });
    onClose();
  });
  const remove = () => { if (window.confirm(`Delete persona “${persona.name}”? Plans tagged with it keep the id but lose the name.`)) actions.act(key, async () => { await deleteEntity('persona', { org: org.id }, persona.id); onClose(); }); };
  const Lines = ({ label, k, rows = 3 }) => <label>{label}<textarea className="cs-input" rows={rows} value={lines(p[k])} onChange={(e) => set(k, splitLines(e.target.value))} /></label>;
  return (
    <section className="cs-card cp-card cp-form">
      <header className="cp-pane-head"><h3>{persona ? `Edit “${persona.name}”` : 'New persona'}</h3><button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={onClose}>Cancel</button></header>
      <div className="cp-grid-2">
        <label>Name<input className="cs-input" value={p.name} onChange={(e) => set('name', e.target.value)} placeholder="The controller" /></label>
        <label>Role<input className="cs-input" value={p.role} onChange={(e) => set('role', e.target.value)} placeholder="Finance controller who owns the ERP risk" /></label>
      </div>
      <label>Spine line <span className="cs-help">· what this person should repeat</span><input className="cs-input" value={p.spineLine} onChange={(e) => set('spineLine', e.target.value)} /></label>
      <div className="cp-grid-2">
        <Lines label="Cares about (one per line)" k="cares" />
        <Lines label="Objects to" k="objections" />
        <Lines label="Proof they want" k="proofWanted" />
        <Lines label="Channels" k="channels" />
      </div>
      {actions.errors[key] && <p className="cp-error">{actions.errors[key]}</p>}
      <div className="cp-actions">
        <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!p.name.trim() || actions.busy[key]} onClick={save}>{actions.busy[key] ? 'Saving…' : 'Save persona'}</button>
        {persona && isAdmin && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline cp-danger" onClick={remove} data-agent-guard="delete persona">Delete</button>}
      </div>
    </section>
  );
}

// Tag / untag personas and segments on one plan.
function PlanAudience({ org, planId, plan, personas, canNote }) {
  const [seg, setSeg] = useState('');
  const ids = plan.personaIds || [];
  const toggle = (id) => updateEntity('contentPlan', { org: org.id }, planId, { personaIds: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].slice(0, 20) });
  const addSeg = () => { const s = seg.trim().slice(0, 80); if (!s) return; updateEntity('contentPlan', { org: org.id }, planId, { segments: [...(plan.segments || []).filter((x) => x !== s), s].slice(0, 20) }); setSeg(''); };
  const dropSeg = (s) => updateEntity('contentPlan', { org: org.id }, planId, { segments: (plan.segments || []).filter((x) => x !== s) });
  return (
    <div className="cp-list">
      <section className="cs-card cp-card">
        <h4>Personas <span className="cs-help">· {ids.length} tagged · the agent writes for these</span></h4>
        {!personas.length ? <p className="cs-help">No personas in this org yet — add them on the module's Audience tab.</p> : null}
        <ul className="cp-cards cp-personas">
          {personas.map((p) => {
            const on = ids.includes(p.id);
            return (
              <li key={p.id} className={`cs-card cp-card cp-persona ${on ? 'is-on' : 'is-off'}`}>
                <header className="cp-pane-head">
                  <div><h3>{p.name}</h3><p className="cs-help">{p.role}</p></div>
                  {canNote && <button type="button" className={`cs-btn cs-btn-sm ${on ? 'cs-btn-outline' : 'cs-btn-primary'}`} onClick={() => toggle(p.id)}>{on ? 'Untag' : 'Tag'}</button>}
                </header>
                {p.spineLine ? <p className="cp-spine">“{p.spineLine}”</p> : null}
                {on ? <PersonaView p={p} /> : null}
              </li>
            );
          })}
        </ul>
      </section>
      <section className="cs-card cp-card">
        <h4>Segments <span className="cs-help">· free-text audience tags</span></h4>
        <div className="cp-segments">
          {(plan.segments || []).map((s) => <span key={s} className="cp-pill is-segment">{s}{canNote ? <button type="button" aria-label={`remove ${s}`} onClick={() => dropSeg(s)}>×</button> : null}</span>)}
          {canNote && <form className="cp-seg-form" onSubmit={(e) => { e.preventDefault(); addSeg(); }}><input className="cs-input" value={seg} onChange={(e) => setSeg(e.target.value)} placeholder="e.g. security reviewer" /><button type="submit" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!seg.trim()}>Add</button></form>}
        </div>
      </section>
    </div>
  );
}
